// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/StaffSearchBar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../api';
import { Search, Loader2, X, Eye, Edit3, ShieldAlert, ShieldCheck, Mail, Phone, ArrowRight } from 'lucide-react';

const StaffSearchBar = ({ 
  searchTerm, 
  onSearchChange, 
  tenantId,
  onQuickEdit,
  onQuickStatusToggle,
  onQuickDelete,
  onViewDetails 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const suggestionRefs = useRef([]);

  // Fetch suggestions based on search term
  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      
      const response = await api.get(
        `/api/admin/staff/${tenantId}?search=${encodeURIComponent(query)}&limit=7`,
        { signal: abortControllerRef.current.signal }
      );

      if (response.data.success) {
        const staffList = response.data.staff || [];
        
        // Filter out tenant_admin
        const filteredSuggestions = staffList.filter(
          staff => staff.role?.toLowerCase() !== 'tenant_admin'
        );
        
        setSuggestions(filteredSuggestions);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching suggestions:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Debounce suggestions fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchSuggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Handle suggestion select
  const handleSuggestionSelect = (suggestion) => {
    onSearchChange(suggestion.firstName + ' ' + suggestion.lastName);
    setShowSuggestions(false);
    if (onViewDetails) {
      onViewDetails(suggestion);
    }
  };

  // Handle quick action
  const handleQuickAction = (e, action, suggestion) => {
    e.stopPropagation();
    
    switch (action) {
      case 'edit':
        if (onQuickEdit) onQuickEdit(suggestion);
        break;
      case 'toggle':
        if (onQuickStatusToggle) onQuickStatusToggle(suggestion._id || suggestion.userId, suggestion.status);
        break;
      case 'delete':
        if (onQuickDelete) onQuickDelete(suggestion._id || suggestion.userId, `${suggestion.firstName} ${suggestion.lastName || ''}`);
        break;
      case 'view':
        if (onViewDetails) onViewDetails(suggestion);
        break;
      default:
        break;
    }
    
    setShowSuggestions(false);
  };

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <mark key={index} className="bg-amber-100 text-amber-950 font-semibold rounded-xs px-0.5">{part}</mark> : 
        part
    );
  };

  return (
    <div className="flex-1 max-w-md relative w-full" ref={wrapperRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
          placeholder="Search staff by name, email, phone..."
          className="w-full h-10 px-3.5 pl-9 pr-9 text-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl outline-hidden focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-all"
          autoComplete="off"
        />
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" />
        
        {/* Loading Spinner */}
        {loading && (
          <div className="absolute right-3 top-3">
            <Loader2 className="animate-spin h-4 w-4 text-slate-400" />
          </div>
        )}

        {/* Clear Button */}
        {searchTerm && !loading && (
          <button
            type="button"
            onClick={() => {
              onSearchChange('');
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 rounded-md transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown Layer Overlay */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200/80 rounded-xl shadow-xl max-h-96 overflow-y-auto divide-y divide-slate-100 animate-slideUp">
          {suggestions.map((suggestion, index) => {
            const displayRole = suggestion.role 
              ? suggestion.role.charAt(0).toUpperCase() + suggestion.role.slice(1).toLowerCase() 
              : '';
              
            return (
              <div
                key={suggestion._id || suggestion.userId || index}
                ref={el => suggestionRefs.current[index] = el}
                className={`p-3 cursor-pointer transition-colors flex items-start gap-3 ${
                  index === selectedIndex ? 'bg-slate-50 border-l-2 border-slate-900' : ''
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* Neutral Monochromatic Avatar */}
                <div className="shrink-0 mt-0.5">
                  <div className="w-8 h-8 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center">
                    <span className="text-slate-700 font-bold text-[11px] tracking-wide">
                      {suggestion.firstName?.charAt(0)}{suggestion.lastName?.charAt(0)}
                    </span>
                  </div>
                </div>
                
                {/* Profile Meta Frame Box */}
                <div className="flex-1 min-w-0 leading-normal">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-slate-900 truncate">
                      {highlightMatch(`${suggestion.firstName} ${suggestion.lastName || ''}`, searchTerm)}
                    </p>
                    
                    {/* Quick Tools Action Bar Row */}
                    <div className="flex items-center gap-0.5 ml-auto shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => handleQuickAction(e, 'view', suggestion)}
                        className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                        title="View profile details"
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleQuickAction(e, 'edit', suggestion)}
                        className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Edit staff record"
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleQuickAction(e, 'toggle', suggestion)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          suggestion.status === 'active' 
                            ? 'text-emerald-600 hover:bg-emerald-50' 
                            : 'text-slate-400 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                        title={suggestion.status === 'active' ? 'Deactivate access' : 'Activate access'}
                      >
                        {suggestion.status === 'active' ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleQuickAction(e, 'delete', suggestion)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                        title="Purge staff record"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Detailed Identification Rows */}
                  <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-slate-400 font-medium">
                    <span className="flex items-center gap-1 truncate">
                      <Mail size={11} className="shrink-0 text-slate-300" />
                      {highlightMatch(suggestion.email, searchTerm)}
                    </span>
                    {suggestion.phoneNumber && (
                      <span className="flex items-center gap-1 truncate">
                        <Phone size={11} className="shrink-0 text-slate-300" />
                        {suggestion.phoneNumber}
                      </span>
                    )}
                  </div>
                  
                  {/* Privilege Badge Row Tags */}
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {displayRole}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${
                      suggestion.status === 'active' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-slate-100 text-slate-500 border border-slate-200'
                    }`}>
                      {suggestion.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Dropdown Deep-Search Footer Controls */}
          <div className="p-1.5 bg-slate-50 border-t border-slate-100 shrink-0">
            <button
              type="button"
              onClick={() => {
                onSearchChange(searchTerm);
                setShowSuggestions(false);
              }}
              className="w-full text-left text-[11px] text-slate-600 hover:text-slate-900 flex items-center justify-between px-2.5 py-1.5 rounded-lg hover:bg-white border border-transparent hover:border-slate-200/60 transition-all font-medium"
            >
              <div className="flex items-center gap-1.5 truncate">
                <Search size={12} className="text-slate-400" />
                <span>Search total ledger index for </span>
                <strong className="text-slate-900 truncate max-w-[120px]">"{searchTerm}"</strong>
              </div>
              <ArrowRight size={12} className="text-slate-400 shrink-0" />
            </button>
          </div>
        </div>
      )}

      {/* Fallback Exception Empty Results Message */}
      {showSuggestions && searchTerm.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl p-5 text-center animate-slideUp">
          <Search size={22} className="mx-auto text-slate-300 mb-2" />
          <p className="text-xs font-bold text-slate-800">No profiles found</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Check search spelling configuration parameters.</p>
          <button
            type="button"
            onClick={() => {
              onSearchChange(searchTerm);
              setShowSuggestions(false);
            }}
            className="mt-3 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 block mx-auto"
          >
            Force query search anyway
          </button>
        </div>
      )}
    </div>
  );
};

export default StaffSearchBar;